import { useEffect } from "react";
import NextLink from "next/link";
import { useRouter } from "next/router";
import type { FlexProps, LinkProps } from "@chakra-ui/react";
import {
  Center,
  Flex,
  Icon,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Stack,
  useDisclosure,
} from "@chakra-ui/react";
import type { IconType } from "react-icons/lib";
import { RiArrowDownSLine } from "react-icons/ri";
import EntityIcons from "components/icons/entity-icons";
import usePermission from "hooks/use-permission";
import colorSchemes from "data/color-schemes";
import ActiveLink from "../app-nav/active-link";

interface SearchLinkProps extends LinkProps {
  href: string;
  children: string;
  icon: IconType;
  iconColor?: string;
  cy?: string;
  isDisabled?: boolean;
}

const SearchLink = ({
  href,
  children,
  icon,
  iconColor,
  cy,
  isDisabled,
}: SearchLinkProps) => {
  const disabledProps: FlexProps = {
    opacity: 0.5,
    cursor: "not-allowed",
    tabIndex: -1,
  };

  const enabledProps: FlexProps = {
    _hover: {
      bg: "gray.100",
    },
    _active: {
      bg: "gray.200",
    },
    _focus: {
      outline: "none",
      shadow: "outline",
    },
  };

  return (
    <Flex
      as={isDisabled ? "div" : NextLink}
      href={href}
      align="center"
      p={2}
      rounded="md"
      fontWeight="medium"
      data-cy={`app-header-link-${cy}`}
      outline="none"
      transition="0.2s all"
      {...(isDisabled ? disabledProps : enabledProps)}
    >
      <Center
        mr={3}
        h={6}
        w={6}
        rounded="md"
        bg={`${iconColor}.100`}
        borderWidth={1}
        borderColor={`${iconColor}.200`}
      >
        <Icon as={icon} color={`${iconColor}.500`} />
      </Center>
      {children}
    </Flex>
  );
};

const SearchDropdown = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const router = useRouter();

  useEffect(() => {
    router.events.on("routeChangeStart", onClose);

    return () => {
      router.events.off("routeChangeStart", onClose);
    };
  }, [router.events, onClose]);

  const [
    hasFundingSearchPermission,
    hasDealSearchPermission,
    hasWealthSearchPermission,
    hasPeopleSearchPermission,
  ] = usePermission([
    "fundingSearch",
    "dealSearch",
    "wealthSearch",
    "peopleSearch",
  ]);

  return (
    <Popover trigger="hover" isOpen={isOpen}>
      <PopoverTrigger>
        <Flex
          onMouseEnter={onOpen}
          onMouseLeave={onClose}
          onFocus={onOpen}
          onBlur={onClose}
          align="center"
          position="relative"
          {...(isOpen && {
            _after: {
              content: "''",
              position: "absolute",
              w: "150%",
              h: "8px",
              bottom: "-8px",
              left: "-33.33%",
            },
          })}
          _focus={{ outline: "none", shadow: "outline" }}
        >
          <ActiveLink href="/search/company">
            <Flex align="center">
              Search <Icon ml={2} as={RiArrowDownSLine} />
            </Flex>
          </ActiveLink>
        </Flex>
      </PopoverTrigger>

      <PopoverContent
        onMouseEnter={onOpen}
        onMouseLeave={onClose}
        onFocus={onOpen}
        onBlur={onClose}
        w="auto"
        shadow="xl"
        rounded="xl"
      >
        <PopoverBody p={2}>
          <Stack alignItems="stretch" spacing={1}>
            <SearchLink
              href="/search/company"
              icon={EntityIcons.company}
              iconColor={colorSchemes.company}
              cy="company-search"
            >
              Company
            </SearchLink>

            <SearchLink
              href="/search/investor"
              icon={EntityIcons.investor}
              iconColor={colorSchemes.investor}
              cy="investor-search"
            >
              Investor
            </SearchLink>

            <SearchLink
              href="/search/funding"
              icon={EntityIcons.funding}
              iconColor={colorSchemes.funding}
              cy="funding-search"
              isDisabled={!hasFundingSearchPermission}
            >
              Funding
            </SearchLink>

            <SearchLink
              href="/search/deal"
              icon={EntityIcons.deal}
              iconColor={colorSchemes.deal}
              cy="deal-search"
              isDisabled={!hasDealSearchPermission}
            >
              Deal
            </SearchLink>

            {hasPeopleSearchPermission && (
              <SearchLink
                href="/search/people"
                icon={EntityIcons.people}
                iconColor={colorSchemes.people}
                cy="people-search"
              >
                People
              </SearchLink>
            )}
            
            {hasWealthSearchPermission && (
              <SearchLink
                href="/search/wealth"
                icon={EntityIcons.wealth}
                iconColor={colorSchemes.wealth}
                cy="wealth-search"
              >
                Wealth
              </SearchLink>
            )}
          </Stack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default SearchDropdown;
